import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { getGroupById, createGroup, updateGroup, getPermissions } from "../services/api";
import { ICONS } from "../icons";
import Button from "../components/UI/Button";
import FloatingInput from "../components/UI/FloatingInput";
import { notify } from "../utils/notify";
import Select from "react-select";
import PageHeader from "../components/UI/PageHeader";

export default function AddEditGroup() {
  const { id } = useParams();
  const navigate = useNavigate();
  const token = localStorage.getItem("token");

  const [name, setName] = useState("");
  const [permissions, setPermissions] = useState([]);
  const [selectedPermissions, setSelectedPermissions] = useState([]);
  const [loading, setLoading] = useState(false);
  
  // توحيد منطق الأخطاء ليكون كائن (Object)
  const [errors, setErrors] = useState({});

  useEffect(() => {
    getPermissions(token)
      .then(res => setPermissions(res.map(p => ({ value: p.id, label: p.name }))))
      .catch(() => notify.error("Failed to load permissions"));

    if (id) {
      getGroupById(token, id).then(g => {
        setName(g.name);
        setSelectedPermissions(g.permissions.map(p => ({ value: p.id, label: p.name })));
      });
    }
  }, [id, token]);

  const handleSubmit = async () => {
    // 1. إعادة تعيين الأخطاء
    setErrors({});
    
    // 2. التحقق الأمامي (Frontend Validation)
    if (!name.trim()) {
      notify.error("Please fill in the required fields correctly");
      setErrors({ name: "Group name is required" });
      
      return;
    }

    setLoading(true);
    const payload = {
      name: name.trim(),
      permissions: selectedPermissions.map(p => p.value),
    };

    try {
      if (id) {
        await updateGroup(token, id, payload);
        notify.success("Group updated successfully");
      } else {
        await createGroup(token, payload);
        notify.success("Group created successfully");
      }
      navigate("/groups");
    } catch (err) {
      // 3. معالجة أخطاء السيرفر (مثلاً: اسم المجموعة مكرر)
      const errorMsg = err.data?.error || err.message || "Something went wrong";
      
      if (errorMsg.toLowerCase().includes("name")) {
        setErrors({ name: errorMsg });
      }
      
      notify.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const customSelectStyles = {
    control: (base) => ({
      ...base,
      padding: '2px',
      borderRadius: '12px',
      borderColor: '#f3f4f6',
      backgroundColor: '#f9fafb80',
      '&:hover': { borderColor: 'var(--primary-color)' },
      boxShadow: 'none',
    }),
    multiValue: (base) => ({
      ...base,
      backgroundColor: 'var(--primary-color)',
      color: 'white',
      borderRadius: '8px',
    }),
    multiValueLabel: (base) => ({ ...base, color: 'white' }),
  };

  return (
    <div className="p-4 md:p-8 max-w-3xl mx-auto">
      {/* Header */}
      <PageHeader className="flex items-center gap-4 mb-8 border-b border-gray-100 pb-6">
        <button
          onClick={() => navigate("/groups")}
          className="p-2 hover:bg-gray-100 rounded-xl transition-colors text-gray-400"
        >
          <ICONS.ChevronLeft className="w-6 h-6 rtl:rotate-180" />
        </button>
        <div>
          <h2 className="text-2xl font-black text-gray-800 tracking-tight">
            {id ? "Edit Security Group" : "Create New Group"}
          </h2>
          <p className="text-gray-400 text-sm italic font-medium">Define access levels and roles</p>
        </div>
      </PageHeader>

      {/* Form Card */}
      <div className="bg-white p-6 md:p-8 rounded-[2.5rem] border border-gray-100 shadow-sm">
        <div className="space-y-8">
          
          <FloatingInput 
            label="Group Name" 
            value={name} 
            onChange={(e) => {
              setName(e.target.value);
              if (errors.name) setErrors({}); // مسح الخطأ فور البدء في التصحيح
            }} 
            error={errors.name} // ربط الخطأ بالحقل
            icon={ICONS.Groups}
          />

          <div className="space-y-3">
            <label className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] ms-4">
              Assign Permissions
            </label>
            <Select 
              options={permissions} 
              value={selectedPermissions} 
              onChange={setSelectedPermissions} 
              isMulti 
              placeholder="Start typing to select permissions..." 
              styles={customSelectStyles}
              className="text-sm"
            />
            <p className="text-[10px] text-gray-400 ms-4 italic">
              * Users in this group will inherit all selected permissions.
            </p>
          </div>

          <div className="flex gap-4 pt-6 border-t border-gray-50">
            <Button 
              variant="primary" 
              onClick={handleSubmit} 
              loading={loading}
              className="flex-grow shadow-lg shadow-blue-500/10"
              icon={id ? ICONS.Save : ICONS.Plus}
            >
              {id ? "Update Group" : "Create Group"}
            </Button>
            
            <Button 
              variant="secondary" 
              onClick={() => navigate("/groups")}
            >
              Cancel
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}